import React, { useState, useEffect, useCallback } from 'react';
import { fetchMinidashboards } from 'api/actions/dashboard';
import { useContractList } from 'context/contracts';
import { SET_META } from 'context/contracts';
import { useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { fetcher } from 'api/client';
import { RadialChart } from 'react-vis';
import clsx from 'clsx';
import { Card, CardBody, CardGroup, CardHeader, Col, ListGroup, ListGroupItem, Media, Row, Badge, CardDeck } from 'reactstrap';


function PieChart({ lhLabel, lhValue, rhLabel, rhValue, index }) {
    const [data, setData] = useState([
        { angle: lhValue || 0, radius: 1.4, innerRadius: 2.2, color: 1 },
        { angle: rhValue || 0, radius: 1.4, innerRadius: 2.2, color: 2 }
    ])
    return (
        <RadialChart height={70} width={70} data={data} colorRange={['#F47B33', '#6c757d']} />
    );
}

function EmptyGraph() {
    return ([...Array(4)].map((i, index) => (
        <Card style={{ minHeight: '140px' }} className={clsx('card-shadow rounded-lg bg-light-gray pulse-animation')} key={`d-box-${index}`}>
        </Card>
    )))
}

function CircleGraph({ handleButtonClick, currentList, mini, t, i, isFetching }) {
    return (
        <Card style={{ minWidth: '300px' }} className={clsx('card-shadow rounded-lg mt-2', isFetching && 'pulse-animation')}>
            <CardBody className="p-3 mx-1">
                <p className="font-weight-bold m-0 mb-3">{t('minidashboard.' + mini.title)}</p>
                <Media>
                    <Media className="mr-2">
                        <PieChart
                            lhLabel={mini.leftHandLabel}
                            lhValue={mini.leftHandValue}
                            rhLabel={mini.rightHandLabel}
                            rhValue={mini.rightHandValue}
                            index={i}
                        />
                    </Media>
                    <Media body className="ml-2 align-self-center">
                        <div className="d-flex">
                            <div>
                                <i className="fas fa-circle text-primary mr-2"></i>
                                <span className="font-weight-bold">{mini.leftHandValue}</span> {t(`minidashboard.${mini.leftHandLabel}`)}
                                <span className="text-muted">{' '}({mini.leftHandPercentage}%)</span>
                            </div>
                            <div className="ml-auto">
                                <Badge
                                    href="#"
                                    className={clsx(currentList === mini.leftHandLabel && 'text-white')}
                                    color={clsx(currentList === mini.leftHandLabel ? 'primary' : 'light')}
                                    onClick={handleButtonClick(mini.leftHandLabel)}
                                >
                                    {t('minidashboard.show')}
                                </Badge>
                            </div>
                        </div>
                        <div className="d-flex mt-3">
                            <div>
                                <i className="fas fa-circle text-muted mr-2"></i>
                                <span className="font-weight-bold">{mini.rightHandValue}</span> {t(`minidashboard.${mini.rightHandLabel}`)}
                                <span className="text-muted">{' '}({mini.rightHandPercentage}%)</span>
                            </div>
                            <div className="ml-auto">
                                <Badge
                                    href="#"
                                    className={clsx(currentList === mini.rightHandLabel && 'text-white')}
                                    color={clsx(currentList === mini.rightHandLabel ? 'primary' : 'light')}
                                    onClick={handleButtonClick(mini.rightHandLabel)}
                                >
                                    {t('minidashboard.show')}
                                </Badge>
                            </div>
                        </div>
                    </Media>
                </Media>
            </CardBody>
        </Card >

    )
}

function ButtonGraph({ handleButtonClick, currentList, mini, t, i, isFetching }) {
    return (
        <Card style={{ minWidth: '300px' }} className={clsx('card-shadow rounded-lg mt-2', isFetching && 'pulse-animation')}>
            <CardBody className="p-2">
                <ListGroup flush className="rounded-lg">
                    {
                        mini.buttons.map((button, index) => (
                            <ListGroupItem key={index}>
                                <div className="d-flex">
                                    <div className="align-self-center">
                                        <span className={clsx('font-weight-bold h5', button.value > 0 && !['MissingVobo'].includes(button.label) && 'text-primary')}>
                                            {button.value}
                                        </span>
                                        <span className="text-muted ml-2">
                                            {t(`minidashboard.${button.label}`)}
                                        </span>
                                    </div>
                                    <div className="ml-auto">
                                        <Badge
                                            href="#"
                                            className={clsx('ml-2', currentList === button.label && 'text-white')}
                                            color={clsx(currentList === button.label ? 'primary' : 'light')}
                                            onClick={handleButtonClick(button.label)}
                                        >
                                            {t('minidashboard.show')}
                                        </Badge>
                                    </div>
                                </div>
                            </ListGroupItem>
                        ))
                    }
                </ListGroup>
            </CardBody>
        </Card>

    )
}

function MiniDashboards({ }) {
    const { t } = useTranslation();
    const [currentList, setCurrentList] = useState('')

    const [state, dispatch] = useContractList()

    const { data, isLoading, error, isFetching } = useQuery('minidashboards-fetch', () => fetcher(fetchMinidashboards()), {
        refetchOnWindowFocus: false,
        enabled: !state.meta.listOnly
    })

    useEffect(() => {
        if (state.meta.url === '/contracts/all') setCurrentList('')
    }, [state.meta.url]);

    const handleButtonClick = useCallback((label) => () => {
        setCurrentList(current => {
            if (current === label) {
                dispatch({
                    type: SET_META,
                    meta: {
                        url: '/contracts/all',
                        page: 1,
                        miniDashboardFilter: ''
                    }
                })
                return ''
            } else {
                dispatch({
                    type: SET_META,
                    meta: {
                        url: `/dashboard/minidashboard/pro/${label}`,
                        page: 1,
                        miniDashboardFilter: label,
                        filter: 'active'
                    }
                })
                return label
            }
        })

    }, [dispatch])

    return (
        <CardDeck style={{ overFlow: 'overlay' }}>
            {isLoading || error ?
                <EmptyGraph />
                :
                data?.map((mini, i) => ({
                    'graph': <CircleGraph key={`${mini.type}-${i}`} currentList={currentList} handleButtonClick={handleButtonClick} mini={mini} t={t} i={i} isFetching={isFetching} />,
                    'buttons': <ButtonGraph key={`${mini.type}-${i}`} currentList={currentList} handleButtonClick={handleButtonClick} mini={mini} t={t} i={i} isFetching={isFetching} />
                }[mini.type]))
            }
        </CardDeck >
    )
}
export default MiniDashboards;